import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../Shared/API/config';

type Recipient = {
  id: string;
  displayName: string;
  kind?: 'school' | 'class' | 'student' | 'staff' | 'level';
};

type Recipients = {
  data: Recipient[];
};

export type MessageRecipientLog = {
  id: string;
  recipient: {
    id: string;
    displayName: string;
  };
  channel: string;
  provider: string | null;
  status: string;
  failureReason: string | null;
  sentAt: string | null;
  deliveredText: string | null;
};

type MessageRecipientLogsResponse = {
  data: MessageRecipientLog[];
};

type MessageApiPayload = {
  recipients: Array<{
    id: string;
    filterType: string;
  }>;
  title: string;
  subject: string;
  sendAt: string;
  content: string;
  status?: string;
  frequency?: string | null;
};

export const messagesApi = createApi({
  reducerPath: 'messagesApi',
  baseQuery,
  tagTypes: ['Message'],
  endpoints: builder => ({
    getMessages: builder.query<
      any,
      { search?: string; messageStatus?: string }
    >({
      query: ({ search, messageStatus }) => {
        const queryParams: string[] = [];

        if (search) {
          queryParams.push(`q=${encodeURIComponent(search)}`);
        }

        if (messageStatus) {
          queryParams.push(`status=${messageStatus}`);
        }

        const queryString = queryParams.length
          ? `?${queryParams.join('&')}`
          : '';

        return `/messaging/messages${queryString}`;
      },
      providesTags: ['Message'],
    }),
    getMessageRecipients: builder.query<
      MessageRecipientLogsResponse,
      { id?: string; search?: string; messageStatus?: string }
    >({
      query: ({ id, search, messageStatus }) => {
        const queryParams: string[] = [];

        if (search) {
          queryParams.push(`q=${encodeURIComponent(search)}`);
        }

        if (messageStatus) {
          queryParams.push(`status=${messageStatus}`);
        }

        const queryString = queryParams.length
          ? `?${queryParams.join('&')}`
          : '';

        return `/messaging/messages/${id}/logs${queryString}`;
      },
      providesTags: ['Message'],
    }),
    getRecipients: builder.query<
      Recipients,
      { search?: string; recipientstatus?: string }
    >({
      query: ({ search, recipientstatus }) => {
        const queryParams: string[] = [];

        if (search) {
          queryParams.push(`q=${encodeURIComponent(search)}`);
        }

        if (recipientstatus) {
          queryParams.push(`status=${recipientstatus}`);
        }

        const queryString = queryParams.length
          ? `?${queryParams.join('&')}`
          : '';

        return `/messaging/recipients${queryString}`;
      },
      providesTags: ['Message'],
    }),
    getTokens: builder.query<any, any>({
      query: () => `/messaging/tokens`,
      providesTags: ['Message'],
    }),

    getMessage: builder.query<any, string>({
      query: id => `/messaging/messages/${id}`,
      providesTags: ['Message'],
    }),
    createMessage: builder.mutation<any, MessageApiPayload>({
      query: newMessage => ({
        url: '/messaging/messages',
        method: 'POST',
        body: newMessage,
      }),
      invalidatesTags: ['Message'],
    }),
    updateMessage: builder.mutation<any, { id: number; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/messaging/messages/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['Message'],
    }),
    scheduleMessage: builder.mutation<any, { id: number; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/messaging/messages/${id}/schedule`,
        method: 'POST',
        body: updates,
      }),
      invalidatesTags: ['Message'],
    }),
    cancelScheduleMessage: builder.mutation<any, string>({
      query: id => ({
        url: `/messaging/messages/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['Message'],
    }),
    deleteMessage: builder.mutation<void, number>({
      query: id => ({
        url: `/messaging/messages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [{ type: 'Message', id }, 'Message'],
    }),
  }),
});

export const {
  useGetMessagesQuery,
  useGetMessageRecipientsQuery,
  useGetRecipientsQuery,
  useGetTokensQuery,
  useGetMessageQuery,
  useCreateMessageMutation,
  useUpdateMessageMutation,
  useScheduleMessageMutation,
  useCancelScheduleMessageMutation,
  useDeleteMessageMutation,
} = messagesApi;
